import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import useStore from "../../../store/useStore";
import { getNotificationDestination } from "../../../utils/notificationNavigation";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const NotificationDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notificationId } = useParams();
  const {
    serverNotifications,
    fetchNotifications,
    markNotificationRead,
  } = useStore();
  const [notification, setNotification] = useState(
    location.state?.notification ?? serverNotifications.find((item) => String(item.id) === String(notificationId)) ?? null,
  );
  const [loading, setLoading] = useState(!notification);
  const [error, setError] = useState("");
  const fetchAttemptedFor = useRef("");

  useEffect(() => {
    const existing = serverNotifications.find((item) => String(item.id) === String(notificationId)) ??
      location.state?.notification;
    if (existing) {
      setNotification(existing);
      setLoading(false);
      setError("");
      return;
    }
    if (fetchAttemptedFor.current === String(notificationId)) return;
    fetchAttemptedFor.current = String(notificationId);

    let active = true;
    setLoading(true);
    fetchNotifications({ page: 1, limit: 100 }).then((list) => {
      if (!active) return;
      const match = list.find((item) => String(item.id) === String(notificationId));
      setNotification(match ?? null);
      setError(match ? "" : "This notification could not be found.");
      setLoading(false);
    }).catch((err) => {
      if (!active) return;
      setError(err.message || "Failed to load this notification.");
      setLoading(false);
    });

    return () => { active = false; };
  }, [notificationId, location.state, serverNotifications, fetchNotifications]);

  useEffect(() => {
    if (notification && !notification.is_read) {
      markNotificationRead(notification.id);
    }
  }, [notification?.id, notification?.is_read, markNotificationRead]);

  const destination = useMemo(
    () => {
      if (!notification) return null;
      const target = getNotificationDestination(notification);
      if (target) return target;
      const data = notification.data ?? notification.metadata ?? {};
      if (String(data.screen ?? "").toUpperCase() === "REFUND_DETAIL" && data.order_id) {
        return `/account/orders/${encodeURIComponent(data.order_id)}`;
      }
      return null;
    },
    [notification],
  );

  const openDestination = () => {
    if (!destination) return;
    if (/^https?:\/\//i.test(destination)) window.location.assign(destination);
    else navigate(destination);
  };

  const fields = notification ? Object.entries(notification) : [];

  return (
    <section className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
      <button
        type="button"
        onClick={() => navigate("/account/notifications")}
        className="mb-5 inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-emerald-300 hover:text-emerald-800"
      >
        <ArrowBackIcon style={{ fontSize: 18 }} />
        Back to notifications
      </button>

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
          Loading notification…
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
          {error}
        </div>
      ) : notification && (
        <article className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-start gap-4 border-b border-gray-100 bg-emerald-50/60 p-5 sm:p-7">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
              <NotificationsNoneOutlinedIcon />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Notification details</p>
              <h1 className="mt-1 break-words text-xl font-extrabold text-gray-900">
                {notification.title || "Notification"}
              </h1>
              {formatDate(notification.created_at ?? notification.timestamp) && (
                <p className="mt-1 text-xs text-gray-500">
                  {formatDate(notification.created_at ?? notification.timestamp)}
                </p>
              )}
            </div>
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${notification.is_read ? "bg-gray-100 text-gray-600" : "bg-emerald-100 text-emerald-800"}`}>
              {notification.is_read ? "Read" : "Unread"}
            </span>
          </div>

          <div className="space-y-6 p-5 sm:p-7">
            {(notification.message || notification.body || notification.description) && (
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400">Message</h2>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-gray-700">
                  {notification.message ?? notification.body ?? notification.description}
                </p>
              </div>
            )}

            {destination && (
              <button
                type="button"
                onClick={openDestination}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-800"
              >
                {String((notification.data ?? notification.metadata ?? {}).screen ?? "").toUpperCase() === "REFUND_DETAIL"
                  ? "View related order"
                  : "Open related page"}
                <OpenInNewIcon style={{ fontSize: 16 }} />
              </button>
            )}

            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400">Notification data</h2>
              <dl className="mt-3 divide-y divide-gray-100 rounded-2xl border border-gray-100">
                {fields.map(([key, value]) => (
                  <div key={key} className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(130px,0.35fr)_1fr] sm:gap-5">
                    <dt className="break-words text-xs font-semibold text-gray-500">{key.replace(/_/g, " ")}</dt>
                    <dd className="min-w-0 break-words text-sm text-gray-800">
                      {typeof value === "object" && value !== null ? (
                        <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-3 text-xs leading-5 text-gray-700">{formatValue(value)}</pre>
                      ) : formatValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </article>
      )}
    </section>
  );
};

export default NotificationDetailPage;
